// This file contains the list of tools that are displayed in the AI Tools
// section of the Teacher Dashboard.

import {t} from "@lingui/core/macro";
import memoize from "lodash.memoize";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {blooketIcon} from "@khan/khanmigo-tools/blooket";

import alarmIcon from "@phosphor-icons/core/regular/alarm.svg";
import anchorIcon from "@phosphor-icons/core/regular/anchor.svg";
import arrowsCounterClockwiseIcon from "@phosphor-icons/core/regular/arrows-counter-clockwise.svg";
import articleIcon from "@phosphor-icons/core/regular/article.svg";
import brainIcon from "@phosphor-icons/core/regular/brain.svg";
import cameraIcon from "@phosphor-icons/core/regular/camera.svg";
import chatCircleTextIcon from "@phosphor-icons/core/regular/chat-circle-text.svg";
import chatsCircleIcon from "@phosphor-icons/core/regular/chats-circle.svg";
import checkSquareIcon from "@phosphor-icons/core/regular/check-square.svg";
import clipboardTextIcon from "@phosphor-icons/core/regular/clipboard-text.svg";
import clockCounterClockwiseIcon from "@phosphor-icons/core/regular/clock-counter-clockwise.svg";
import envelopeOpenIcon from "@phosphor-icons/core/regular/envelope-open.svg";
import examIcon from "@phosphor-icons/core/regular/exam.svg";
import featherIcon from "@phosphor-icons/core/regular/feather.svg";
import globeSimpleIcon from "@phosphor-icons/core/regular/globe-simple.svg";
import lightbulbFilamentIcon from "@phosphor-icons/core/regular/lightbulb-filament.svg";
import linkIcon from "@phosphor-icons/core/regular/link.svg";
import magnifyingGlassPlusIcon from "@phosphor-icons/core/regular/magnifying-glass-plus.svg";
import newspaperIcon from "@phosphor-icons/core/regular/newspaper.svg";
import pencilIcon from "@phosphor-icons/core/regular/pencil.svg";
import rulerIcon from "@phosphor-icons/core/regular/ruler.svg";
import sealQuestionIcon from "@phosphor-icons/core/regular/seal-question.svg";
import stepsIcon from "@phosphor-icons/core/regular/steps.svg";
import strategyIcon from "@phosphor-icons/core/regular/strategy.svg";
import textColumnsIcon from "@phosphor-icons/core/regular/text-columns.svg";
import ticketIcon from "@phosphor-icons/core/regular/ticket.svg";

export enum ToolCategories {
    All = "All Tools",
    Favorites = "My Favorites",
    Plan = "Plan",
    Create = "Create",
    Differentiate = "Differentiate",
    Support = "Support",
    Learn = "Learn",
}

export function i18nToolCategory(toolCategory: ToolCategories) {
    switch (toolCategory) {
        case ToolCategories.Favorites:
            return t`My Favorites`;
        case ToolCategories.Plan:
            return t`Plan`;
        case ToolCategories.Create:
            return t`Create`;
        case ToolCategories.Differentiate:
            return t`Differentiate`;
        case ToolCategories.Support:
            return t`Support`;
        case ToolCategories.Learn:
            return t`Learn`;
        default:
            return t`All Tools`;
    }
}

export function toCEDARToolCategory(toolCategory: ToolCategories) {
    switch (toolCategory) {
        case ToolCategories.Favorites:
            return "MY_FAVORITES";
        case ToolCategories.Plan:
            return "PLAN";
        case ToolCategories.Create:
            return "CREATE";
        case ToolCategories.Differentiate:
            return "DIFFERENTIATE";
        case ToolCategories.Support:
            return "SUPPORT";
        case ToolCategories.Learn:
            return "LEARN";
        default:
            return "ALL";
    }
}

// Specifies which layout a particular teacher tool should present.
// Previously we assumed that if chatOnly was false we'd use the coeditor, but
// with Blooket can no longer make that assumption.
export enum Layout {
    Blooket = "Blooket",
    ChatOnly = "ChatOnly",
    Coeditor = "Coeditor",
    EditAssignment = "EditAssignment",
}

export type Tool = {
    urlSlug: string;
    configName: string;
    title: string;
    description: string;
    categories: Array<ToolCategories>;
    icon: PhosphorRegular;
    color: string;
    layout: Layout;
    extensionOrder?: number;
    activitySlug?: string;
    reportingLabelId: string;
    requireClassroomSelection?: boolean;
    requireDistrictSelection?: boolean;
    footer?: string;
};

// List of tools that are displayed in the Khanmigo Tools tab on the Teacher
// Dashboard. These tools are alphabetized except for the "Chat with GPT-4" tool
// which should be at the end of the list.
// sync-start:teacher-tools 2077736545 https://github.com/Khan/webapp/blob/master/libs/guide-core/src/internal/tool-paths.ts
export const getTools: () => Tool[] = memoize(() => [
    {
        urlSlug: "assign-multiple-choice-practice",
        configName: "teacher-tool-assign-multiple-choice-practice",
        title: t`Assign Multiple Choice`,
        description: t`Create multiple-choice quizzes on a variety of topics.`,
        categories: [ToolCategories.Create, ToolCategories.Plan],
        icon: checkSquareIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        reportingLabelId: "assign-multiple-choice-practice",
    },
    {
        urlSlug: "blooket-generator",
        configName: "teacher-tool-blooket-generator",
        title: t`Blooket Generator`,
        description: t`Create Blooket questions on a variety of topics.`,
        categories: [ToolCategories.Create],
        icon: blooketIcon,
        color: color.fadedGreen24,
        layout: Layout.Blooket,
        reportingLabelId: "multiple-choice-blooket",
    },
    {
        urlSlug: "chunk-text",
        configName: "teacher-tool-chunk-text",
        title: t`Chunk Text`,
        description: t`Break complex texts into manageable sections for easier student comprehension.`,
        categories: [
            ToolCategories.Differentiate,
            ToolCategories.Create,
            ToolCategories.Support,
        ],
        icon: textColumnsIcon,
        color: color.fadedRed24,
        layout: Layout.Coeditor,
        reportingLabelId: "chunk-text",
    },
    {
        urlSlug: "class-newsletter",
        configName: "teacher-tool-class-newsletter",
        title: t`Class Newsletter`,
        description: t`Produce engaging newsletters to keep parents informed about class activities.`,
        categories: [ToolCategories.Create],
        icon: newspaperIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        activitySlug: "activity-write-class-newsletter",
        reportingLabelId: "write-class-newsletter",
    },
    {
        urlSlug: "class-snapshot",
        configName: "activity-class-snapshot",
        title: t`Class Snapshot`,
        description: t`Review your students' performance and trends.`,
        categories: [
            ToolCategories.Plan,
            ToolCategories.Learn,
            ToolCategories.Differentiate,
        ],
        icon: cameraIcon,
        color: color.fadedPurple24,
        layout: Layout.ChatOnly,
        activitySlug: "activity-class-snapshot",
        requireClassroomSelection: true,
        reportingLabelId: "class-snapshot",
    },
    {
        urlSlug: "clear-directions",
        configName: "teacher-tool-clear-directions",
        title: t`Clear Directions`,
        description: t`Generate concise, easy-to-follow instructions for assignments and activities.`,
        categories: [
            ToolCategories.Create,
            ToolCategories.Plan,
            ToolCategories.Support,
        ],
        icon: stepsIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        reportingLabelId: "clear-directions",
    },
    {
        urlSlug: "discussion-prompts",
        configName: "teacher-tool-discussion-prompts",
        title: t`Discussion Prompts`,
        description: t`Craft engaging prompts to stimulate meaningful classroom discussions.`,
        categories: [ToolCategories.Plan, ToolCategories.Create],
        icon: chatsCircleIcon,
        color: color.fadedPurple24,
        layout: Layout.Coeditor,
        reportingLabelId: "discussion-prompts",
    },
    {
        urlSlug: "exit-ticket",
        configName: "teacher-tool-exit-ticket",
        title: t`Exit Ticket`,
        description: t`Create quick end-of-lesson assessments to check student understanding.`,
        categories: [
            ToolCategories.Plan,
            ToolCategories.Create,
            ToolCategories.Support,
        ],
        icon: ticketIcon,
        color: color.fadedPurple24,
        layout: Layout.Coeditor,
        extensionOrder: 5,
        activitySlug: "activity-write-exit-ticket-questions",
        reportingLabelId: "write-exit-ticket",
    },
    {
        urlSlug: "fun-class-summary-poem",
        configName: "teacher-tool-fun-class-summary-poem",
        title: t`Fun Class Summary Poem`,
        description: t`Transform memorable class moments into a creative poetic recap.`,
        categories: [ToolCategories.Create],
        icon: featherIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        activitySlug: "activity-create-class-summary-poem",
        reportingLabelId: "write-summary-poem",
    },
    {
        urlSlug: "iep-assistant",
        configName: "teacher-tool-iep-assistant",
        title: t`IEP Assistant`,
        description: t`Streamline the creation of Individualized Education Plans.`,
        categories: [ToolCategories.Support],
        icon: strategyIcon,
        color: color.fadedBlue24,
        layout: Layout.Coeditor,
        reportingLabelId: "iep-assistant",
        footer: t`Note: This output is a draft and does not constitute a complete or final IEP. It requires further refinement based on your professional judgment, evaluation, and analysis. Be sure that the final IEP is tailored to the student's specific needs and complies with all applicable school policies and legal requirements.`,
    },
    {
        urlSlug: "informational-text",
        configName: "teacher-tool-informational-text",
        title: t`Informational Text`,
        description: t`Create informational text for a variety of topics.`,
        categories: [ToolCategories.Create, ToolCategories.Differentiate],
        icon: articleIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        reportingLabelId: "informational-text",
    },
    {
        urlSlug: "learning-objectives",
        configName: "teacher-tool-learning-objectives",
        title: t`Learning Objective(s)`,
        description: t`Develop clear, measurable learning objectives to guide instruction.`,
        categories: [
            ToolCategories.Plan,
            ToolCategories.Create,
            ToolCategories.Support,
        ],
        icon: magnifyingGlassPlusIcon,
        color: color.fadedPurple24,
        layout: Layout.Coeditor,
        activitySlug: "activity-write-learning-objectives",
        reportingLabelId: "write-learning-objectives",
    },
    {
        urlSlug: "lesson-hook",
        configName: "teacher-tool-lesson-hook",
        title: t`Lesson Hook`,
        description: t`Plan compelling lesson starters to engage students.`,
        categories: [ToolCategories.Plan, ToolCategories.Create],
        icon: anchorIcon,
        color: color.fadedPurple24,
        layout: Layout.Coeditor,
        activitySlug: "activity-create-a-lesson-hook",
        reportingLabelId: "create-lesson-hook",
    },
    {
        urlSlug: "lesson-plan",
        configName: "teacher-tool-create-lesson-plan",
        title: t`Lesson Plan`,
        description: t`Create structured, detailed lesson plans tailored to your curriculum and students' needs.`,
        categories: [ToolCategories.Plan, ToolCategories.Create],
        icon: pencilIcon,
        color: color.fadedPurple24,
        layout: Layout.Coeditor,
        extensionOrder: 3,
        activitySlug: "activity-create-lesson-plan",
        reportingLabelId: "create-lesson-plan",
    },
    {
        urlSlug: "letter-of-recommendation",
        configName: "teacher-tool-letter-of-recommendation",
        title: t`Letter of Recommendation`,
        description: t`Create personalized letters of recommendation.`,
        categories: [ToolCategories.Create],
        icon: envelopeOpenIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        activitySlug: "activity-write-recommendation-letter",
        reportingLabelId: "write-recommendation-letter",
    },
    {
        urlSlug: "leveler",
        configName: "teacher-tool-leveler",
        title: t`Leveler`,
        description: t`Adjust the complexity of a given text.`,
        categories: [
            ToolCategories.Differentiate,
            ToolCategories.Create,
            ToolCategories.Support,
        ],
        icon: rulerIcon,
        color: color.fadedRed24,
        layout: Layout.Coeditor,
        reportingLabelId: "text-levler",
    },
    {
        urlSlug: "make-it-relevant",
        configName: "teacher-tool-make-it-relevant",
        title: t`Make it Relevant!`,
        description: t`Link lesson content to students' lives and interests to boost engagement.`,
        categories: [ToolCategories.Differentiate, ToolCategories.Support],
        icon: linkIcon,
        color: color.fadedRed24,
        layout: Layout.Coeditor,
        reportingLabelId: "make-it-relevant",
    },
    {
        urlSlug: "multiple-choice-quiz",
        configName: "teacher-tool-multiple-choice-quiz",
        title: t`Multiple Choice Quiz`,
        description: t`Create multiple-choice quizzes on a variety of topics.`,
        categories: [ToolCategories.Create, ToolCategories.Plan],
        icon: checkSquareIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        reportingLabelId: "multiple-choice-assessment",
    },
    {
        urlSlug: "questions-generator",
        configName: "teacher-tool-questions-generator",
        title: t`Questions Generator`,
        description: t`Create questions for a specific piece of content.`,
        categories: [ToolCategories.Create, ToolCategories.Plan],
        icon: sealQuestionIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        reportingLabelId: "questions-generator",
    },
    {
        urlSlug: "real-world-context-generator",
        configName: "teacher-tool-real-world-context-generator",
        title: t`Real World Context Generator`,
        description: t`Connect lesson topics to engaging real-world examples and applications.`,
        categories: [
            ToolCategories.Differentiate,
            ToolCategories.Create,
            ToolCategories.Support,
        ],
        icon: globeSimpleIcon,
        color: color.fadedRed24,
        layout: Layout.Coeditor,
        reportingLabelId: "real-world-context-generator",
    },
    {
        urlSlug: "recommend-assignments",
        configName: "activity-assignment-recommendations",
        title: t`Recommend Assignments`,
        description: t`Receive recommendations on what your students should work on next.`,
        categories: [
            ToolCategories.Plan,
            ToolCategories.Learn,
            ToolCategories.Differentiate,
        ],
        icon: lightbulbFilamentIcon,
        color: color.fadedPurple24,
        layout: Layout.ChatOnly,
        activitySlug: "activity-assignment-recommendations",
        requireClassroomSelection: true,
        reportingLabelId: "assignment-recommendations",
    },
    {
        urlSlug: "refresh-my-knowledge",
        configName: "activity-refresh-my-knowledge",
        title: t`Refresh My Knowledge`,
        description: t`Refresh your content knowledge in various subject areas.`,
        categories: [ToolCategories.Learn],
        icon: brainIcon,
        color: color.fadedGold24,
        layout: Layout.ChatOnly,
        extensionOrder: 2,
        activitySlug: "activity-refresh-my-knowledge",
        reportingLabelId: "refresh-my-knowledge-teacher",
    },
    {
        urlSlug: "report-card-comments",
        configName: "teacher-tool-report-card-comments",
        title: t`Report Card Comments`,
        description: t`Generate personalized, constructive report card comments.`,
        categories: [ToolCategories.Create, ToolCategories.Support],
        icon: examIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        reportingLabelId: "report-card-comments",
    },
    {
        urlSlug: "rubric-generator",
        configName: "activity-create-a-rubric",
        title: t`Rubric Generator`,
        description: t`Design clear, detailed grading rubrics to set expectations and simplify scoring.`,
        categories: [ToolCategories.Create, ToolCategories.Plan],
        icon: clipboardTextIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        extensionOrder: 4,
        activitySlug: "activity-create-a-rubric",
        reportingLabelId: "co-create-rubric",
    },
    {
        urlSlug: "smart-goal-writer",
        configName: "teacher-tool-smart-goal-writer",
        title: t`SMART Goal Writer`,
        description: t`Create specific, measurable, achievable, relevant, and time-bound goals.`,
        categories: [ToolCategories.Support],
        icon: alarmIcon,
        color: color.fadedBlue24,
        layout: Layout.Coeditor,
        reportingLabelId: "smart-goal-writer",
    },
    {
        urlSlug: "summarize-student-chat-history",
        configName: "activity-get-chat-summary-for-teacher",
        title: t`Summarize Student Chat History`,
        description: t`Gain insights from compiled summaries of student Khanmigo chats.`,
        categories: [ToolCategories.Support, ToolCategories.Learn],
        icon: clockCounterClockwiseIcon,
        color: color.fadedBlue24,
        layout: Layout.ChatOnly,
        activitySlug: "activity-get-chat-summary-for-teacher",
        requireClassroomSelection: true,
        reportingLabelId: "summarize-chat-history-teacher",
    },
    {
        urlSlug: "text-rewriter",
        configName: "teacher-tool-text-rewriter",
        title: t`Text Rewriter`,
        description: t`Customize text to meet your instructional needs.`,
        categories: [
            ToolCategories.Differentiate,
            ToolCategories.Create,
            ToolCategories.Support,
        ],
        icon: arrowsCounterClockwiseIcon,
        color: color.fadedRed24,
        layout: Layout.Coeditor,
        reportingLabelId: "text-rewriter",
    },
    {
        urlSlug: "chat-with-gpt",
        configName: "activity-chat-with-gpt",
        title: t`Chat with GPT`,
        description: t`Have a conversation with the GPT-4o mini model.`,
        categories: [ToolCategories.Learn],
        icon: chatCircleTextIcon,
        color: color.fadedGold24,
        layout: Layout.ChatOnly,
        extensionOrder: 1,
        activitySlug: "activity-chat-with-gpt",
        reportingLabelId: "chat-with-gpt4-teacher",
    },
]);
// sync-end:teacher-tools

// sync-start:parent-tools 2028163478 https://github.com/Khan/webapp/blob/master/libs/guide-core/src/internal/tool-paths.ts
export const getParentTools: () => Tool[] = memoize(() => {
    const tools = [
        {
            urlSlug: "blooket-generator",
            configName: "teacher-tool-blooket-generator",
            title: t`Blooket Generator`,
            description: t`Create Blooket questions on a variety of topics.`,
            categories: [ToolCategories.Create],
            icon: blooketIcon,
            color: color.fadedGreen24,
            layout: Layout.Blooket,
            reportingLabelId: "multiple-choice-blooket",
        },
        {
            urlSlug: "chunk-text",
            configName: "teacher-tool-chunk-text",
            title: t`Chunk Text`,
            description: t`Break complex texts into manageable sections for easier student comprehension.`,
            categories: [
                ToolCategories.Differentiate,
                ToolCategories.Create,
                ToolCategories.Support,
            ],
            icon: textColumnsIcon,
            color: color.fadedRed24,
            layout: Layout.Coeditor,
            reportingLabelId: "chunk-text",
        },
        {
            urlSlug: "class-newsletter",
            configName: "teacher-tool-class-newsletter",
            title: t`Class Newsletter`,
            description: t`Produce engaging newsletters to keep parents informed about class activities.`,
            categories: [ToolCategories.Create],
            icon: newspaperIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            activitySlug: "activity-write-class-newsletter",
            reportingLabelId: "write-class-newsletter",
        },
        {
            urlSlug: "class-snapshot",
            configName: "activity-class-snapshot",
            title: t`Class Snapshot`,
            description: t`Review your students' performance and trends.`,
            categories: [
                ToolCategories.Plan,
                ToolCategories.Learn,
                ToolCategories.Differentiate,
            ],
            icon: cameraIcon,
            color: color.fadedPurple24,
            layout: Layout.ChatOnly,
            activitySlug: "activity-class-snapshot",
            requireClassroomSelection: true,
            reportingLabelId: "class-snapshot",
        },
        {
            urlSlug: "class-snapshot-parent",
            configName: "activity-class-snapshot-for-parent",
            title: t`Class Snapshot`,
            description: t`Review your children's performance and trends.`,
            categories: [
                ToolCategories.Plan,
                ToolCategories.Learn,
                ToolCategories.Differentiate,
            ],
            icon: cameraIcon,
            color: color.fadedPurple24,
            layout: Layout.ChatOnly,
            activitySlug: "activity-class-snapshot-for-parent",
            requireClassroomSelection: true,
            reportingLabelId: "class-snapshot-parent",
        },
        {
            urlSlug: "clear-directions",
            configName: "teacher-tool-clear-directions",
            title: t`Clear Directions`,
            description: t`Generate concise, easy-to-follow instructions for assignments and activities.`,
            categories: [
                ToolCategories.Create,
                ToolCategories.Plan,
                ToolCategories.Support,
            ],
            icon: stepsIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            reportingLabelId: "clear-directions",
        },
        {
            urlSlug: "discussion-prompts",
            configName: "teacher-tool-discussion-prompts",
            title: t`Discussion Prompts`,
            description: t`Craft engaging prompts to stimulate meaningful classroom discussions.`,
            categories: [ToolCategories.Plan, ToolCategories.Create],
            icon: chatsCircleIcon,
            color: color.fadedPurple24,
            layout: Layout.Coeditor,
            reportingLabelId: "discussion-prompts",
        },
        {
            urlSlug: "exit-ticket",
            configName: "teacher-tool-exit-ticket",
            title: t`Exit Ticket`,
            description: t`Create quick end-of-lesson assessments to check student understanding.`,
            categories: [
                ToolCategories.Plan,
                ToolCategories.Create,
                ToolCategories.Support,
            ],
            icon: ticketIcon,
            color: color.fadedPurple24,
            layout: Layout.Coeditor,
            extensionOrder: 5,
            activitySlug: "activity-write-exit-ticket-questions",
            reportingLabelId: "write-exit-ticket",
        },
        {
            urlSlug: "fun-class-summary-poem",
            configName: "teacher-tool-fun-class-summary-poem",
            title: t`Fun Class Summary Poem`,
            description: t`Transform memorable class moments into a creative poetic recap.`,
            categories: [ToolCategories.Create],
            icon: featherIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            activitySlug: "activity-create-class-summary-poem",
            reportingLabelId: "write-summary-poem",
        },
        {
            urlSlug: "iep-assistant",
            configName: "teacher-tool-iep-assistant",
            title: t`IEP Assistant`,
            description: t`Streamline the creation of Individualized Education Plans.`,
            categories: [ToolCategories.Support],
            icon: strategyIcon,
            color: color.fadedBlue24,
            layout: Layout.Coeditor,
            reportingLabelId: "iep-assistant",
            footer: t`Note: This output is a draft and does not constitute a complete or final IEP. It requires further refinement based on your professional judgment, evaluation, and analysis. Be sure that the final IEP is tailored to the student's specific needs and complies with all applicable school policies and legal requirements.`,
        },
        {
            urlSlug: "informational-text",
            configName: "teacher-tool-informational-text",
            title: t`Informational Text`,
            description: t`Create informational text for a variety of topics.`,
            categories: [ToolCategories.Create, ToolCategories.Differentiate],
            icon: articleIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            reportingLabelId: "informational-text",
        },
        {
            urlSlug: "learning-objectives",
            configName: "teacher-tool-learning-objectives",
            title: t`Learning Objective(s)`,
            description: t`Develop clear, measurable learning objectives to guide instruction.`,
            categories: [
                ToolCategories.Plan,
                ToolCategories.Create,
                ToolCategories.Support,
            ],
            icon: magnifyingGlassPlusIcon,
            color: color.fadedPurple24,
            layout: Layout.Coeditor,
            activitySlug: "activity-write-learning-objectives",
            reportingLabelId: "write-learning-objectives",
        },
        {
            urlSlug: "lesson-hook",
            configName: "teacher-tool-lesson-hook",
            title: t`Lesson Hook`,
            description: t`Plan compelling lesson starters to engage students.`,
            categories: [ToolCategories.Plan, ToolCategories.Create],
            icon: anchorIcon,
            color: color.fadedPurple24,
            layout: Layout.Coeditor,
            activitySlug: "activity-create-a-lesson-hook",
            reportingLabelId: "create-lesson-hook",
        },
        {
            urlSlug: "lesson-plan",
            configName: "teacher-tool-create-lesson-plan",
            title: t`Lesson Plan`,
            description: t`Create structured, detailed lesson plans tailored to your curriculum and students' needs.`,
            categories: [ToolCategories.Plan, ToolCategories.Create],
            icon: pencilIcon,
            color: color.fadedPurple24,
            layout: Layout.Coeditor,
            extensionOrder: 3,
            activitySlug: "activity-create-lesson-plan",
            reportingLabelId: "create-lesson-plan",
        },
        {
            urlSlug: "letter-of-recommendation",
            configName: "teacher-tool-letter-of-recommendation",
            title: t`Letter of Recommendation`,
            description: t`Create personalized letters of recommendation.`,
            categories: [ToolCategories.Create],
            icon: envelopeOpenIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            activitySlug: "activity-write-recommendation-letter",
            reportingLabelId: "write-recommendation-letter",
        },
        {
            urlSlug: "leveler",
            configName: "teacher-tool-leveler",
            title: t`Leveler`,
            description: t`Adjust the complexity of a given text.`,
            categories: [
                ToolCategories.Differentiate,
                ToolCategories.Create,
                ToolCategories.Support,
            ],
            icon: rulerIcon,
            color: color.fadedRed24,
            layout: Layout.Coeditor,
            reportingLabelId: "text-levler",
        },
        {
            urlSlug: "make-it-relevant",
            configName: "teacher-tool-make-it-relevant",
            title: t`Make it Relevant!`,
            description: t`Link lesson content to students' lives and interests to boost engagement.`,
            categories: [ToolCategories.Differentiate, ToolCategories.Support],
            icon: linkIcon,
            color: color.fadedRed24,
            layout: Layout.Coeditor,
            reportingLabelId: "make-it-relevant",
        },
        {
            urlSlug: "multiple-choice-quiz",
            configName: "teacher-tool-multiple-choice-quiz",
            title: t`Multiple Choice Quiz`,
            description: t`Create multiple-choice quizzes on a variety of topics.`,
            categories: [ToolCategories.Create, ToolCategories.Plan],
            icon: checkSquareIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            reportingLabelId: "multiple-choice-assessment",
        },
        {
            urlSlug: "questions-generator",
            configName: "teacher-tool-questions-generator",
            title: t`Questions Generator`,
            description: t`Create questions for a specific piece of content.`,
            categories: [ToolCategories.Create, ToolCategories.Plan],
            icon: sealQuestionIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            reportingLabelId: "questions-generator",
        },
        {
            urlSlug: "real-world-context-generator",
            configName: "teacher-tool-real-world-context-generator",
            title: t`Real World Context Generator`,
            description: t`Connect lesson topics to engaging real-world examples and applications.`,
            categories: [
                ToolCategories.Differentiate,
                ToolCategories.Create,
                ToolCategories.Support,
            ],
            icon: globeSimpleIcon,
            color: color.fadedRed24,
            layout: Layout.Coeditor,
            reportingLabelId: "real-world-context-generator",
        },
        {
            urlSlug: "recommend-assignments",
            configName: "activity-assignment-recommendations",
            title: t`Recommend Assignments`,
            description: t`Receive recommendations on what your students should work on next.`,
            categories: [
                ToolCategories.Plan,
                ToolCategories.Learn,
                ToolCategories.Differentiate,
            ],
            icon: lightbulbFilamentIcon,
            color: color.fadedPurple24,
            layout: Layout.ChatOnly,
            activitySlug: "activity-assignment-recommendations",
            requireClassroomSelection: true,
            reportingLabelId: "assignment-recommendations",
        },
        {
            urlSlug: "recommend-assignments-parent",
            configName: "activity-assignment-recommendations-for-parent",
            title: t`Recommend Assignments`,
            description: t`Receive recommendations on what your children should work on next.`,
            categories: [
                ToolCategories.Plan,
                ToolCategories.Learn,
                ToolCategories.Differentiate,
            ],
            icon: lightbulbFilamentIcon,
            color: color.fadedPurple24,
            layout: Layout.ChatOnly,
            activitySlug: "activity-assignment-recommendations-for-parent",
            requireClassroomSelection: true,
            reportingLabelId: "assignment-recommendations-parent",
        },
        {
            urlSlug: "refresh-my-knowledge",
            configName: "activity-refresh-my-knowledge",
            title: t`Refresh My Knowledge`,
            description: t`Refresh your content knowledge in various subject areas.`,
            categories: [ToolCategories.Learn],
            icon: brainIcon,
            color: color.fadedGold24,
            layout: Layout.ChatOnly,
            extensionOrder: 2,
            activitySlug: "activity-refresh-my-knowledge",
            reportingLabelId: "refresh-my-knowledge-teacher",
        },
        {
            urlSlug: "refresh-my-knowledge-for-parent",
            configName: "activity-refresh-my-knowledge-for-parent",
            title: t`Refresh My Knowledge`,
            description: t`Refresh your content knowledge in various subject areas.`,
            categories: [ToolCategories.Learn],
            icon: brainIcon,
            color: color.fadedGold24,
            layout: Layout.ChatOnly,
            extensionOrder: 2,
            activitySlug: "activity-refresh-my-knowledge-for-parent",
            reportingLabelId: "refresh-my-knowledge-parent",
        },
        {
            urlSlug: "report-card-comments",
            configName: "teacher-tool-report-card-comments",
            title: t`Report Card Comments`,
            description: t`Generate personalized, constructive report card comments.`,
            categories: [ToolCategories.Create, ToolCategories.Support],
            icon: examIcon,
            color: color.fadedGreen24,
            layout: Layout.Coeditor,
            reportingLabelId: "report-card-comments",
        },
        {
            urlSlug: "rubric-generator",
            configName: "activity-create-a-rubric",
            title: t`Rubric Generator`,
            description: t`Design clear, detailed grading rubrics to set expectations and simplify scoring.`,
            categories: [ToolCategories.Create, ToolCategories.Plan],
            icon: clipboardTextIcon,
            color: color.fadedGreen24,
            layout: Layout.ChatOnly,
            extensionOrder: 4,
            activitySlug: "activity-create-a-rubric",
            reportingLabelId: "co-create-rubric",
        },
        {
            urlSlug: "smart-goal-writer",
            configName: "teacher-tool-smart-goal-writer",
            title: t`SMART Goal Writer`,
            description: t`Create specific, measurable, achievable, relevant, and time-bound goals.`,
            categories: [ToolCategories.Support],
            icon: alarmIcon,
            color: color.fadedBlue24,
            layout: Layout.Coeditor,
            reportingLabelId: "smart-goal-writer",
        },
        {
            urlSlug: "summarize-student-chat-history",
            configName: "activity-get-chat-summary-for-teacher",
            title: t`Summarize Student Chat History`,
            description: t`Gain insights from compiled summaries of student Khanmigo chats.`,
            categories: [ToolCategories.Support, ToolCategories.Learn],
            icon: clockCounterClockwiseIcon,
            color: color.fadedBlue24,
            layout: Layout.ChatOnly,
            activitySlug: "activity-get-chat-summary-for-teacher",
            requireClassroomSelection: true,
            reportingLabelId: "summarize-chat-history-teacher",
        },
        {
            urlSlug: "summarize-child-chat-history-for-parent",
            configName: "activity-get-chat-summary-for-parent",
            title: t`Summarize child chat history`,
            description: t`Gain insights from compiled summaries of child Khanmigo chats.`,
            categories: [ToolCategories.Support, ToolCategories.Learn],
            icon: clockCounterClockwiseIcon,
            color: color.fadedBlue24,
            layout: Layout.ChatOnly,
            activitySlug: "activity-get-chat-summary-for-parent",
            requireClassroomSelection: false,
            reportingLabelId: "summarize-chat-history-parent",
        },
        {
            urlSlug: "text-rewriter",
            configName: "teacher-tool-text-rewriter",
            title: t`Text Rewriter`,
            description: t`Customize text to meet your instructional needs.`,
            categories: [
                ToolCategories.Differentiate,
                ToolCategories.Create,
                ToolCategories.Support,
            ],
            icon: arrowsCounterClockwiseIcon,
            color: color.fadedRed24,
            layout: Layout.Coeditor,
            reportingLabelId: "text-rewriter",
        },
        {
            urlSlug: "chat-with-gpt",
            configName: "activity-chat-with-gpt",
            title: t`Chat with GPT`,
            description: t`Have a conversation with the GPT-4o mini model.`,
            categories: [ToolCategories.Learn],
            icon: chatCircleTextIcon,
            color: color.fadedGold24,
            layout: Layout.ChatOnly,
            extensionOrder: 1,
            activitySlug: "activity-chat-with-gpt",
            reportingLabelId: "chat-with-gpt4-teacher",
        },
        {
            urlSlug: "chat-with-gpt-for-parent",
            configName: "activity-chat-with-gpt-for-parent",
            title: t`Chat with GPT`,
            description: t`Have a conversation with the GPT-4o mini model.`,
            categories: [ToolCategories.Learn],
            icon: chatCircleTextIcon,
            color: color.fadedGold24,
            layout: Layout.ChatOnly,
            extensionOrder: 1,
            activitySlug: "activity-chat-with-gpt-for-parent",
            reportingLabelId: "chat-with-gpt4-parent",
        },
    ];

    return tools;
});
// sync-end:parent-tools

// sync-start:admin-tools 812307983 https://github.com/Khan/webapp/blob/master/libs/guide-core/src/internal/tool-paths.ts
export const getAdminTools: () => Tool[] = memoize(() => [
    {
        urlSlug: "course-snapshot",
        configName: "admin-course-snapshot",
        title: t`Course Snapshot`,
        description: t`Get a summary of students progress in a course across one or multiple schools.`,
        categories: [
            ToolCategories.Plan,
            ToolCategories.Learn,
            ToolCategories.Differentiate,
        ],
        icon: cameraIcon,
        color: color.fadedPurple24,
        layout: Layout.ChatOnly,
        extensionOrder: 4,
        activitySlug: "admin-course-snapshot",
        reportingLabelId: "admin-course-snapshot",
        requireClassroomSelection: true,
    },
    {
        urlSlug: "chat-with-gpt",
        configName: "admin-tool-chat-with-gpt",
        title: t`Chat with GPT`,
        description: t`Have a conversation with the GPT-4o mini model.`,
        categories: [ToolCategories.Learn],
        icon: chatCircleTextIcon,
        color: color.fadedGold24,
        layout: Layout.ChatOnly,
        extensionOrder: 1,
        activitySlug: "admin-tool-chat-with-gpt",
        reportingLabelId: "chat-with-gpt4-admin",
    },
    {
        urlSlug: "smart-goal-writer",
        configName: "admin-tool-smart-goal-writer",
        title: t`SMART Goal Writer`,
        description: t`Create specific, measurable, achievable, relevant, and time-bound goals.`,
        categories: [ToolCategories.Support],
        icon: alarmIcon,
        color: color.fadedBlue24,
        layout: Layout.Coeditor,
        reportingLabelId: "smart-goal-writer-admin",
    },
    {
        urlSlug: "refresh-my-knowledge",
        configName: "admin-tool-refresh-my-knowledge",
        title: t`Refresh My Knowledge`,
        description: t`Refresh your content knowledge in various subject areas.`,
        categories: [ToolCategories.Learn],
        icon: brainIcon,
        color: color.fadedGold24,
        layout: Layout.ChatOnly,
        extensionOrder: 2,
        activitySlug: "admin-tool-refresh-my-knowledge",
        reportingLabelId: "refresh-my-knowledge-admin",
    },
    {
        urlSlug: "letter-of-recommendation",
        configName: "admin-tool-letter-of-recommendation",
        title: t`Letter of Recommendation`,
        description: t`Create personalized letters of recommendation.`,
        categories: [ToolCategories.Create],
        icon: envelopeOpenIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        activitySlug: "admin-tool-letter-of-recommendation",
        reportingLabelId: "write-recommendation-letter-admin",
    },
    {
        urlSlug: "iep-assistant",
        configName: "admin-tool-iep-assistant",
        title: t`IEP Assistant`,
        description: t`Streamline the creation of Individualized Education Plans.`,
        categories: [ToolCategories.Support],
        icon: strategyIcon,
        color: color.fadedBlue24,
        layout: Layout.Coeditor,
        reportingLabelId: "iep-assistant-admin",
        footer: t`Note: This output is a draft and does not constitute a complete or final IEP. It requires further refinement based on your professional judgment, evaluation, and analysis. Be sure that the final IEP is tailored to the student's specific needs and complies with all applicable school policies and legal requirements.`,
    },
    {
        urlSlug: "school-newsletter",
        configName: "admin-school-newsletter",
        title: t`School Newsletter`,
        description: t`Produce engaging newsletters to keep teachers informed about school activities.`,
        categories: [ToolCategories.Create],
        icon: newspaperIcon,
        color: color.fadedGreen24,
        layout: Layout.Coeditor,
        requireDistrictSelection: true,
        reportingLabelId: "admin-school-newsletter",
    },
]);
// sync-end:admin-tools

export const getToolsByConfigName = (): Record<string, Tool> =>
    getTools().reduce((obj, tool) => ({...obj, [tool.configName]: tool}), {});

export const getToolsByUrlSlug = (): Record<string, Tool> =>
    getTools().reduce((obj, tool) => ({...obj, [tool.urlSlug]: tool}), {});

export const getToolsByActivitySlug = (): Record<string, Tool> =>
    getTools().reduce(
        (obj, tool) =>
            tool.activitySlug ? {...obj, [tool.activitySlug]: tool} : obj,
        {},
    );
export const getExtensionTools = (): Tool[] =>
    getTools()
        .filter((tool) => typeof tool.extensionOrder !== "undefined")
        .sort(
            (a, b) =>
                (a.extensionOrder as number) - (b.extensionOrder as number),
        );

export const getParentToolsByUrlSlug = (): Record<string, Tool> =>
    getParentTools().reduce(
        (obj, tool) => (tool.urlSlug ? {...obj, [tool.urlSlug]: tool} : obj),
        {},
    );
export const getParentToolsByActivitySlug = (): Record<string, Tool> =>
    getParentTools().reduce(
        (obj, tool) =>
            tool.activitySlug ? {...obj, [tool.activitySlug]: tool} : obj,
        {},
    );

export const getAdminToolsByUrlSlug = (): Record<string, Tool> =>
    getAdminTools().reduce(
        (obj, tool) => (tool.urlSlug ? {...obj, [tool.urlSlug]: tool} : obj),
        {},
    );

export const getAdminToolsByConfigName = (): Record<string, Tool> =>
    getAdminTools()
        .concat(getTools())
        .reduce((obj, tool) => ({...obj, [tool.configName]: tool}), {});
